/**
 * The editor's taxonomy facade — one shape, two disciplines.
 *
 * The graph editor canvas is discipline-agnostic (a box, a polygon, a line), but
 * the Category / Subcategory / Type dropdowns are not: a P&ID offers valves and
 * vessels, an SLD offers breakers and buses. This module is the single seam
 * between them, so a panel asks for "the active taxonomy" instead of importing
 * `pnidClassification` directly.
 *
 * Level values are typed `string` here on purpose. Each sheet has its own
 * closed union (`PnidLevelI`, `SldLevelI`) and they are not interchangeable, so
 * the shared surface widens to `string` and each taxonomy narrows internally.
 * That keeps a panel from having to know which discipline it is rendering.
 *
 * See the SLD annotation design doc for why SLD is a sibling of P&ID rather
 * than a third member of the `pnid | pfd` document-type family.
 */

import * as pnid from './pnidClassification'
import * as sld from './sldClassification'

/** A drawing discipline the editor can annotate. */
export type DrawingDiscipline = 'pnid' | 'sld'

/**
 * The function surface every panel in the editor needs.
 *
 * Deliberately the INTERSECTION of the two sheets, not the union: a P&ID-only
 * concern (vision-category forward mapping) stays out, so a panel cannot
 * silently depend on something SLD has no answer for.
 */
export interface Taxonomy {
  readonly discipline: DrawingDiscipline
  /** All Level I categories, in display order. */
  readonly levelIValues: readonly string[]
  /** The Level I exempt from needing a Subcategory/Type to be approved, if any. */
  readonly annotationLevelI: string
  /**
   * True when the node KIND can only be decided once the Level III leaf is
   * picked.
   *
   * P&ID resolves at Level II (`Piping ▸ Line` is a connection whatever leaf
   * follows). The SLD sheet resolves from the leaf's `geometry` column, and its
   * `Line` group is mixed — Feeder and Cable are lines, Line Crossing and Line
   * Break are boxes — so Level II alone is genuinely ambiguous.
   *
   * Callers that CONVERT a node's kind must not act on an incomplete
   * classification when this is true: a half-picked `Electrical ▸ Line` resolves
   * to `symbol`, and converting a drawn connection to a symbol clears its
   * `geometry_wkt`. The line's geometry is then gone on save.
   */
  readonly resolvesNodeTypeFromLeaf: boolean
  getLevelII(levelI: string): string[]
  getLevelIII(levelI: string, levelII: string): string[]
  resolveNodeType(
    levelI: string,
    levelII: string,
    levelIII?: string | null,
  ): 'symbol' | 'tag' | 'connection'
  categoryOptionsForNodeType(nodeType: string): string[]
  isMissingRequiredType(category: string, subcategory: string, type: string): boolean
  getEntryNotes(levelI: string, levelII: string, levelIII: string | null): string | undefined
  /**
   * The classification to SEED the panels with for a node that has no saved
   * `pnid_level_*` — a detected node, or a shape drawn without picking one.
   *
   * Must return levels that exist in THIS taxonomy. The P&ID implementation
   * guesses from the legacy vision `category`; returning its guesses on an SLD
   * would seed `Equipment` / `Piping ▸ Line`, which the SLD dropdowns cannot
   * display — the select renders blank while state holds a P&ID value, and the
   * next field blur persists it onto an electrical node.
   */
  resolveClassification(
    nodeType: 'symbol' | 'tag' | 'connection',
    category?: string,
  ): { levelI: string; levelII: string; levelIII: string }
}

const PNID_TAXONOMY: Taxonomy = {
  discipline: 'pnid',
  levelIValues: pnid.PNID_LEVEL_I_VALUES,
  annotationLevelI: pnid.ANNOTATION_LEVEL_I,
  resolvesNodeTypeFromLeaf: false,
  getLevelII: (l1) => pnid.getLevelII(l1 as pnid.PnidLevelI),
  getLevelIII: (l1, l2) => pnid.getLevelIII(l1 as pnid.PnidLevelI, l2),
  resolveNodeType: (l1, l2, l3) => pnid.resolveNodeType(l1 as pnid.PnidLevelI, l2, l3),
  categoryOptionsForNodeType: (t) => pnid.categoryOptionsForNodeType(t),
  isMissingRequiredType: (c, s, t) => pnid.isMissingRequiredType(c, s, t),
  getEntryNotes: (l1, l2, l3) => pnid.getEntryNotes(l1 as pnid.PnidLevelI, l2, l3),
  resolveClassification: (nodeType, category) => pnid.resolveClassification(nodeType, category),
}

const SLD_TAXONOMY: Taxonomy = {
  discipline: 'sld',
  levelIValues: sld.SLD_LEVEL_I_VALUES,
  // Unlike P&ID, the SLD Annotation branch is NOT exempt from needing a
  // Subcategory/Type — every one of its leaves can be picked. The empty string
  // can never equal a picked category, which is exactly the intent.
  annotationLevelI: '',
  resolvesNodeTypeFromLeaf: true,
  getLevelII: (l1) => sld.getLevelII(l1 as sld.SldLevelI),
  getLevelIII: (l1, l2) => sld.getLevelIII(l1 as sld.SldLevelI, l2),
  resolveNodeType: (l1, l2, l3) => sld.resolveNodeType(l1 as sld.SldLevelI, l2, l3),
  categoryOptionsForNodeType: (t) => sld.categoryOptionsForNodeType(t),
  // The SLD sheet has no null Level III, so a missing leaf is always a real gap.
  isMissingRequiredType: (c, s, t) => sld.isMissingRequiredType(c, s, t),
  getEntryNotes: (l1, l2, l3) =>
    l3 == null ? undefined : sld.getEntryNotes(l1 as sld.SldLevelI, l2, l3),
  // Every value below exists in the SLD sheet, which is the whole point — a
  // seed the dropdowns cannot display is worse than no seed, because the panel
  // then shows an empty select over state that a field blur will persist.
  //
  // The legacy vision `category` is deliberately ignored: it is a P&ID-vision
  // token (`valve`, `instrument`, `exchanger`) and an SLD has no vision tier to
  // produce one. There is nothing to map from.
  resolveClassification: (nodeType) => {
    if (nodeType === 'connection') {
      // Level III left empty: `Line` carries six leaves and guessing between a
      // feeder, a cable and a control line would be inventing an answer. Safe
      // to leave incomplete because `resolvesNodeTypeFromLeaf` stops a caller
      // converting the node off this half-picked seed.
      return { levelI: 'Electrical', levelII: 'Line', levelIII: '' }
    }
    if (nodeType === 'tag') {
      return { levelI: 'Annotation', levelII: 'Text', levelIII: 'Tag' }
    }
    return { levelI: 'Electrical', levelII: '', levelIII: '' }
  },
}

/** The taxonomy for a discipline. Defaults to P&ID, which is every existing drawing. */
export function taxonomyFor(discipline?: DrawingDiscipline | null): Taxonomy {
  return discipline === 'sld' ? SLD_TAXONOMY : PNID_TAXONOMY
}

/**
 * Whether a stored classification still exists in the given taxonomy.
 *
 * A saved node keeps its levels as plain strings, so a taxonomy edit can strand
 * them: the sheet drops or renames a leaf, and the panel's sanitizer then
 * silently blanks that node's Category/Subcategory/Type on the next edit —
 * losing an annotator's work with no message.
 *
 * Callers use this to WARN rather than to auto-correct —
 * silently rewriting someone's classification is the failure being guarded
 * against, not the fix.
 */
export function isKnownClassification(
  taxonomy: Taxonomy,
  levelI?: string | null,
  levelII?: string | null,
  levelIII?: string | null,
): boolean {
  const l1 = (levelI ?? '').trim()
  if (!l1) return true // unclassified is a valid state, not a stranded one
  if (!taxonomy.levelIValues.includes(l1)) return false
  const l2 = (levelII ?? '').trim()
  if (!l2) return true
  if (!taxonomy.getLevelII(l1).includes(l2)) return false
  const l3 = (levelIII ?? '').trim()
  if (!l3) return true
  return taxonomy.getLevelIII(l1, l2).includes(l3)
}

/**
 * Which discipline a graph document belongs to.
 *
 * Reads `properties.source_document_type`, the field the graph is stamped with
 * when it is created (`'sld'` by `SldService`, `'pnid'` / `'pfd'` by the parse
 * pipeline). Anything else — including a graph created before this field
 * existed — is a P&ID, which is the safe default: it is what every graph in
 * every tenant is today.
 */
export function disciplineFromProperties(
  properties?: Record<string, unknown> | null,
): DrawingDiscipline {
  return properties?.['source_document_type'] === 'sld' ? 'sld' : 'pnid'
}

/**
 * True when `properties` actually carries a discipline stamp.
 *
 * `disciplineFromProperties` answers `'pnid'` both for "this is a P&ID" and for
 * "I do not know" — correct as a default, but it means a caller cannot tell the
 * two apart. A caller with a SECOND source to fall back to (the parent
 * document's type) needs the difference: an empty or stamp-less `properties`
 * object is truthy, so testing the container rather than the field makes that
 * fallback unreachable.
 */
export function hasDisciplineStamp(properties?: Record<string, unknown> | null): boolean {
  return typeof properties?.['source_document_type'] === 'string'
}

export { PNID_TAXONOMY, SLD_TAXONOMY }
